import * as React from 'react';
import { NS } from '../../../common/namespaces';
import SvgEditService from '../../../services/svg-edit-service';
import PanelsService from '../../../services/panels-service';
import { ComponentRuleCondition, ComponentRuleAction, IRulesEntry, IRulesEntryCondition, IRulesEntryAction } from './componentrules';
import styles from './ComponentRulesEditor.module.scss';

interface IComponentRulesState {
    rules: IRulesEntry[];
}

export class PDTagEditor_ComponentRules extends React.Component<{}, IComponentRulesState> {
    state: IComponentRulesState = {
        rules: []
    };

    private svgEditService = SvgEditService.Get(this, () => this.onSelectionChanged());
    private panelService = PanelsService.Get(this);

    public componentDidMount() {
        this.onSelectionChanged(); // Trigger on first load
    }

    public componentWillUnmount() {
        this.svgEditService.UnregisterComponent(this);
        this.panelService.UnregisterComponent(this);
    }

    private onSelectionChanged = () => {
        const elements = this.svgEditService.selectedElements;

        if (elements.length != 1 || !elements[0]) {
            this.setState({ rules: [] });
            return;
        }

        const element = elements[0];
        const newState: IComponentRulesState = {
            rules: [],
        }
        const rulesStr = element.getAttribute("zaa:component-rules");

        if (rulesStr && rulesStr.length > 0) {
            const parsedRules = JSON.parse(rulesStr);

            if (parsedRules && Array.isArray(parsedRules)) {
                newState.rules = parsedRules as IRulesEntry[]; // TODO: This should probably be validated more before casting.
            } else {
                console.error("Could not parse rules: " + rulesStr);
            }
        }

        this.setState(newState);
    }

    private onStateChanged = (newState: IComponentRulesState) => {
        this.svgEditService.canvas.changeSelectedAttribute("zaa:component-rules", JSON.stringify(newState.rules), null);
        this.setState(newState);
    }

    private addRule = () => {
        let newState: IComponentRulesState = {
            rules: cloneRules(this.state.rules)
        };

        newState.rules.push({
            conds: [{
                term: "nothing",
                op: "eq",
                val: "",
            }],
            actions: [{
                action: "nothing",
                arg: "",
            }]
        });
        this.onStateChanged(newState);
    }

    private removeRule = (index: number) => {
        if (index < 0 || index >= this.state.rules.length) {
            return;
        }

        let newState: IComponentRulesState = {
            rules: cloneRules(this.state.rules)
        };

        newState.rules.splice(index, 1);
        this.onStateChanged(newState);
    }

    private addCondition = (ruleIndex: number) => {
        if (ruleIndex < 0 || ruleIndex >= this.state.rules.length) {
            return;
        }

        let newState: IComponentRulesState = {
            rules: cloneRules(this.state.rules)
        };

        newState.rules[ruleIndex].conds.push({
            term: "nothing",
            op: "eq",
            val: "",
        });
        this.onStateChanged(newState);
    }

    private removeCondition = (ruleIndex: number, condIndex: number) => {
        if (ruleIndex < 0 || ruleIndex >= this.state.rules.length) {
            return;
        }

        if (condIndex < 0 || condIndex >= this.state.rules[ruleIndex].conds.length) {
            return;
        }

        let newState: IComponentRulesState = {
            rules: cloneRules(this.state.rules)
        };

        newState.rules[ruleIndex].conds.splice(condIndex, 1);
        this.onStateChanged(newState);
    }


    private addAction = (ruleIndex: number) => {
        if (ruleIndex < 0 || ruleIndex >= this.state.rules.length) {
            return;
        }

        let newState: IComponentRulesState = {
            rules: cloneRules(this.state.rules)
        };

        newState.rules[ruleIndex].actions.push({
            action: "nothing",
            arg: "",
        });
        this.onStateChanged(newState);
    }

    private removeAction = (ruleIndex: number, actionIndex: number) => {
        if (ruleIndex < 0 || ruleIndex >= this.state.rules.length) {
            return;
        }

        if (actionIndex < 0 || actionIndex >= this.state.rules[ruleIndex].actions.length) {
            return;
        }

        let newState: IComponentRulesState = {
            rules: cloneRules(this.state.rules)
        };

        newState.rules[ruleIndex].actions.splice(actionIndex, 1);
        this.onStateChanged(newState);
    }


    private onConditionChange(newCondition: IRulesEntryCondition, ruleIndex: number, condIndex: number) {
        if (ruleIndex < 0 || ruleIndex > this.state.rules.length) {
            return;
        }

        if (condIndex < 0 || condIndex > this.state.rules[ruleIndex].conds.length) {
            return;
        }

        let newState: IComponentRulesState = {
            rules: cloneRules(this.state.rules)
        };

        newState.rules[ruleIndex].conds[condIndex] = newCondition;
        this.onStateChanged(newState);
    }

    private onActionChange(newAction: IRulesEntryAction, ruleIndex: number, actionIndex: number) {
        if (ruleIndex < 0 || ruleIndex > this.state.rules.length) {
            return;
        }

        if (actionIndex < 0 || actionIndex > this.state.rules[ruleIndex].actions.length) {
            return;
        }

        let newState: IComponentRulesState = {
            rules: cloneRules(this.state.rules)
        };

        newState.rules[ruleIndex].actions[actionIndex] = newAction;
        this.onStateChanged(newState);
    }

    public render() {
        const getID = (p: string) => `pd-tagEditor__rules__${p}`;

        if (!this.state.rules?.map) {
            return (
                <div className={styles.container}>
                    <h1>Component Rules</h1>
                    <p>Please select a component</p>
                </div>
            );
        }

        return (
            <div className={styles.container}>
                <h1>Component Rules</h1>

                {this.state.rules.map((r, ri) => (
                    <div key={`rule_${ri}`} id={getID(`rule_${ri}`)} className={styles.entry}>
                        <header>
                            <span>Rule {ri + 1}</span>
                            <button type="button" className="btn btn-danger" onClick={() => this.removeRule(ri)}>Remove Rule</button>
                        </header>
                        <table>
                            <tbody>
                                {r.conds.map((c, ci) => (
                                    <ComponentRuleCondition key={`rule_${ri}_cond_${ci}`} cond={c} ruleIndex={ri} condIndex={ci}
                                        panelService={this.panelService}
                                        onChange={(nc, ncri, nci) => this.onConditionChange(nc, ncri, nci)}
                                        onRemove={(rcri, rci) => this.removeCondition(rcri, rci)} />
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={999} className={styles.entryButtons}>
                                        <button type="button" className="btn btn-primary" onClick={e => this.addCondition(ri)}>Add Condition</button>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                        <hr />
                        <table>
                            <tbody>
                                {r.actions.map((a, ai) => (
                                    <ComponentRuleAction key={`rule_${ri}_action_${ai}`} action={a} ruleIndex={ri} actionIndex={ai}
                                        panelService={this.panelService}
                                        onChange={(na, nari, nai) => this.onActionChange(na, nari, nai)}
                                        onRemove={(rari, rai) => this.removeAction(rari, rai)} />
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={999} className={styles.entryButtons}>
                                        <button type="button" className="btn btn-primary" onClick={e => this.addAction(ri)}>Add Action</button>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                ))}

                <div className={styles.entryButtons}>
                    <button type="button" className="btn btn-primary" onClick={e => this.addRule()}>Add New Rule</button>
                </div>
            </div>
        );
    }
}

function cloneRules(rules: IRulesEntry[]): IRulesEntry[] {
    return JSON.parse(JSON.stringify(rules)) as IRulesEntry[]
}
